import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import { Organization, Project, WorkItemResponse } from "src/types";
import { useRestAPI } from "./axiosAPI";
import { useAzureApis } from "./azureAPIs";
import { WorkItemRevision } from "./central.workItem";

const QUERY_KEY = 'Central-Data';

export type OrganizationResponse = {
    count: number;
    value: ({
        accountId: string;
        accountUri: string;
        accountName: string;
        properties: {};
    })[];
};

export type UserCacheType = {
    user: {
        subjectKind: string;
        metaType: string;
        directoryAlias: string;
        domain: string;
        principalName: string;
        mailAddress: string;
        origin: string;
        originId: string;
        displayName: string;
        _links: {
            self: { href: string };
            memberships: { href: string };
            membershipState: { href: string };
            storageKey: { href: string };
            avatar: { href: string };
        };
        url: string;
        descriptor: string;
    };
    extensions: Array<unknown>;
    id: string[];
    accessLevel: {
        licensingSource: string;
        accountLicenseType: string;
        msdnLicenseType: string;
        licenseDisplayName: string;
        status: string;
        statusMessage: string;
        assignmentSource: string;
    };
    lastAccessedDate: string;
    dateCreated: string;
    projectEntitlements: Array<unknown>;
    groupAssignments: Array<unknown>;
    projects: ({ id: string; totalWorkItem: number; completedWorkItem: number })[];
    organizations: ({ id: string; totalWorkItem: number; completedWorkItem: number })[];
    totalWorkItem: number;
    completedWorkItem: number;
};


export interface WorkItemRevisionsResponse {
    count: number;
    value: WorkItemRevision[];
}

export type WorkItemTypeCentral = {
    id: number;
    revisions: WorkItemRevision[];
    organizationId: string | undefined;
    
};

export function useCentralAPI({
    retryCount,
    trigger = true,

}: {
    retryCount?: number;
    trigger?: boolean;
}) {
    const api = useRestAPI();
    const { getWorkItem } = useAzureApis();
    const userData = useSelector((state: RootState) => state.user.adminDetails);

    async function loadOrganizations({ memberId }: { memberId: string }) {
        const response = await api.get<OrganizationResponse>({
            url: `https://app.vssps.visualstudio.com/_apis/accounts`,
            mode: 'protected',
            params: {
                memberId: memberId,
                'api-version': process.env.REACT_APP_API_VERSION,
            },
        });
        if (response.success === false) {
            throw response.msg;
        }
        return response.value;
    }

    async function loadOrganizationsProjects({ organization }: { organization: string }) {
        type ProjectResponse = {
            count: number;
            value: ({
                id: string;
                name: string;
                url: string;
                state: string;
                revision: number;
                visibility: string;
                lastUpdateTime: string;
            })[];
        };

        const response = await api.get<ProjectResponse>({
            url: `https://dev.azure.com/${organization}/_apis/projects`,
            mode: 'protected',
            params: {
                'api-version': process.env.REACT_APP_API_VERSION,
            },
        });
        if (response.success === false) {
            throw response.msg;
        }
        return response.value;
    }

    async function loadScopeDistributorUnderProject({ organization, projectId }: { organization: string; projectId: string }) {
        type ScopeResponse = {
            value: string;
        };

        const response = await api.get<ScopeResponse>({
            url: `https://vssps.dev.azure.com/${organization}/_apis/graph/descriptors/${projectId}`,
            mode: 'protected',
            params: {
                'api-version': process.env.REACT_APP_API_VERSION,
            },
        });
        if (response.success === false) {
            throw response.msg;
        }
        return response.value;
    }

    async function loadOrganizationsUsers({ organization }: { organization: string }) {
        type UsersResponse = {
            items: [
                {
                    user: {
                        subjectKind: string;
                        metaType: string;
                        directoryAlias: string;
                        domain: string;
                        principalName: string;
                        mailAddress: string;
                        origin: string;
                        originId: string;
                        displayName: string;
                        _links: {
                            self: { href: string };
                            memberships: { href: string };
                            membershipState: { href: string };
                            storageKey: { href: string };
                            avatar: { href: string };
                        };
                        url: string;
                        descriptor: string;
                    };
                    extensions: Array<unknown>;
                    id: string;
                    accessLevel: {
                        licensingSource: string;
                        accountLicenseType: string;
                        msdnLicenseType: string;
                        licenseDisplayName: string;
                        status: string;
                        statusMessage: string;
                        assignmentSource: string;
                    };
                    lastAccessedDate: string;
                    dateCreated: string;
                    projectEntitlements: Array<unknown>;
                    groupAssignments: Array<unknown>;
                }
            ];
        };

        const response = await api.get<UsersResponse>({
            url: `https://vsaex.dev.azure.com/${organization}/_apis/userentitlements`,
            mode: 'protected',
            params: {
                'api-version': process.env.REACT_APP_API_VERSION,
            },
        });
        if (response.success === false) {
            throw response.msg;
        }
        return response.items;
    }

    async function loadUsersUnderProject({ organization, scopeDescriptor }: { organization: string; scopeDescriptor: string }) {
        type UsersResponse = {
            count: number;
            value: [
                {
                    subjectKind: string;
                    directoryAlias: string;
                    domain: string;
                    principalName: string;
                    mailAddress: string;
                    origin: string;
                    originId: string;
                    displayName: string;
                    _links: {
                        self: { href: string };
                        memberships: { href: string };
                        membershipState: { href: string };
                        storageKey: { href: string };
                        avatar: { href: string };
                    };
                    url: string;
                    descriptor: string;
                }
            ];
        };

        const response = await api.get<UsersResponse>({
            url: `https://vssps.dev.azure.com/${organization}/_apis/graph/users`,
            mode: 'protected',
            params: {
                'api-version': '7.2-preview.1',
                scopeDescriptor: scopeDescriptor,
            },
        });
        if (response.success === false) {
            throw response.msg;
        }
        return response.value;
    }

    async function loadWorkItemsByQuery({ organization }: { organization: string }) {
        type WorkItemQueryResponse = {
            workItems: { id: number; url: string }[];
            columns: { referenceName: string; name: string; url: string }[];
            queryResultType: string;
            asOf: string;
        };

        const wiql = `
            SELECT [System.Id], [System.Title], [System.State], [System.AssignedTo], [System.WorkItemType]
            FROM workitems
        `;

        const response = await api.post<WorkItemQueryResponse, {}>({
            url: `https://dev.azure.com/${organization}/_apis/wit/wiql`,
            mode: 'protected',
            data: { query: wiql },
            params: {
                'api-version': process.env.REACT_APP_API_VERSION,
            },
        });

        if (response.success === false) {
            throw response.msg;
        }
        return response.workItems;
    }

    async function loadWorkItemDetailsBatch({ organization, projectName, workItemIds }: { organization: string; projectName: string; workItemIds: number[] }) {
        type BatchResponse = {
            count: number;
            value: WorkItemResponse[];
        };

        const response = await api.post<BatchResponse, {}>({
            url: `https://dev.azure.com/${organization}/${projectName}/_apis/wit/workitemsbatch`,
            mode: 'protected',
            data: {
                ids: workItemIds,
                fields: [
                    'System.Id',
                    'System.Title',
                    'System.State',
                    'System.AssignedTo',
                    'System.WorkItemType',
                    'System.TeamProject',
                ],
            },
            params: {
                'api-version': process.env.REACT_APP_API_VERSION,
            },
        });

        if (response.success === false) {
            throw response.msg;
        }
        return response.value;
    }
    async function loadWorkItemRevisions({ organization, workItemId }: { organization: string; workItemId: number }) {

        const response = await api.get<WorkItemRevisionsResponse>({
            url: `https://dev.azure.com/${organization}/_apis/wit/workItems/${workItemId}/revisions?api-version=7.0&$expand=relations`,
            mode: 'protected',
        })

        if (response.success === false) {
            throw response.msg;
        }
        return response.value;
    }

    async function loadProjectStatus(organizationName: string, projectName: string, workItems: { id: number; fields: { [key: string]: any } }[]): Promise<'completed' | 'inProgress' | 'onHold' | 'dropped'> {
        const statusWorkItems = workItems.filter(
            (wi) =>
                wi.fields['System.WorkItemType'] === 'Epic' &&
                wi.fields['System.Title']?.includes('PROJECT STATUS:-') &&
                wi.fields['System.State'] !== 'Done' &&
                wi.fields['System.TeamProject'] === projectName
        );

        if (statusWorkItems.length > 0) {
            const statusString = statusWorkItems[0].fields['System.Title'];
            const status = statusString.split('PROJECT STATUS:- ')[1]?.toUpperCase();

            if (status === 'ONHOLD') return 'onHold';
            if (status === 'COMPLETED') return 'completed';
            if (status === 'DROPPED') return 'dropped';
        }

        const uncompletedWorkItems = workItems.filter(
            (wi) => wi.fields['System.State'] !== 'Done' && wi.fields['System.TeamProject'] === projectName
        );
        return uncompletedWorkItems.length === 0 ? 'completed' : 'inProgress';
    }

    const {
        data: centralData,
        isLoading,
        isError,
        refetch: refetchProductCategories,
        isFetching,
        isFetched,
        error,
    } = useQuery({
        queryKey: [QUERY_KEY],
        queryFn: async () => {
            // Load organizations
            const organizations = await loadOrganizations({ memberId: userData?.id! });
            const Organizations: Organization[] = organizations.map((organization) => ({
                ...organization,
                projects: [],
                users: [],
                totalWorkItem: 0,
                completedWorkItem: 0,
            } as Organization));

            // Load all users and work items for each organization
            const Users: UserCacheType[] = [];
            const organizationWorkItemsMap: { [orgName: string]: {id: number; revisions: WorkItemRevision[]; }[] } = {};

            console.log('first')
            await Promise.all(
                Organizations.map(async (organization) => {
                    // Load users
                    const users = await loadOrganizationsUsers({ organization: organization.accountName });

                    // Load all work item IDs for the organization using WIQL
                    const workItemRefs = await loadWorkItemsByQuery({ organization: organization.accountName });

                    // Fetch work item details in batches (up to 200 per request)
                    const workItems: { id: number; revisions: WorkItemRevision[] }[] = [];
                    // for (let i = 0; i < workItemRefs.length; i += 200) {
                    //     const batchIds = workItemRefs.slice(i, i + 200).map((wi) => wi.id);
                    //     const batchDetails = await loadWorkItemDetailsBatch({
                    //         organization: organization.accountName,
                    //         projectName: '', // Project name not required for batch API
                    //         workItemIds: batchIds,
                    //     });
                    //     workItems.push(...batchDetails.map((item) => ({ id: item.id,rev: item.rev, fields: item.fields })));
                    // }

                    // Process workItemRefs in batches with a delay between batches
                    const batchSize = 500; // Adjust based on testing (e.g., 5-10 requests at a time)

                    const processBatch = async (batch: {
                        id: number;
                        url: string;
                    }[]) => {
                        const batchPromises = batch.map(async (workItemRef) => {
                            const batchDetails = await loadWorkItemRevisions({
                                organization: organization.accountName,
                                workItemId: workItemRef.id
                            });
                            return {
                                id: workItemRef.id,
                                revisions: batchDetails,
                            };
                        });
                        return (await Promise.all(batchPromises)).flat();
                    };

                    // Process workItemRefs in chunks with a delay
                    for (let i = 0; i < workItemRefs.length; i += batchSize) {
                        const batch = workItemRefs.slice(i, i + batchSize);
                        const batchResults = await processBatch(batch);
                        workItems.push(...batchResults);
                        
                    }
                    organizationWorkItemsMap[organization.accountName] = workItems ;

                    // Calculate total and completed work items for organization
                    organization.totalWorkItem = workItems.length;
                    organization.completedWorkItem = workItems.filter((wi) => wi.revisions[wi.revisions.length - 1].fields['System.State'] === 'Done').length;

                    // Assign users to organization and populate Users array
                    users.forEach((user) => {
                        organization.users.push({ originId: user.user.originId });
                        const userAlreadyIndex = Users.findIndex((u) => u.user.originId === user.user.originId);
                        if (userAlreadyIndex >= 0) {
                            Users[userAlreadyIndex].organizations.push({
                                id: organization.accountId,
                                totalWorkItem: organization.totalWorkItem,
                                completedWorkItem: organization.completedWorkItem,
                            });
                            Users[userAlreadyIndex].id.push(user.id);
                            return;
                        }
                        Users.push({
                            ...user,
                            projects: [],
                            organizations: [
                                {
                                    id: organization.accountId,
                                    totalWorkItem: organization.totalWorkItem,
                                    completedWorkItem: organization.completedWorkItem,
                                },
                            ],
                            completedWorkItem: 0,
                            totalWorkItem: 0,
                            id: [user.id],
                        });
                    });
                })
            );
            console.log('second')
            // Calculate work items per user across organizations
            await Promise.all(
                Users.map(async (user) => {
                    const organizations = Organizations.filter((org) => user.organizations.some((o) => o.id === org.accountId));
                    organizations.forEach((organization) => {
                        const workItems = organizationWorkItemsMap[organization.accountName] || [];
                        const userWorkItems = workItems.filter(
                            (wi) => wi.revisions[wi.revisions.length - 1].fields['System.AssignedTo']?.uniqueName === user.user.mailAddress
                        );
                        const completedWorkItems = userWorkItems.filter((wi) => wi.revisions[wi.revisions.length - 1].fields['System.State'] === 'Done');
                        const orgIndex = user.organizations.findIndex((org) => org.id === organization.accountId);
                        if (orgIndex >= 0) {
                            user.organizations[orgIndex].totalWorkItem = userWorkItems.length;
                            user.organizations[orgIndex].completedWorkItem = completedWorkItems.length;
                        }
                    });
                    user.totalWorkItem = user.organizations.reduce((total, org) => total + org.totalWorkItem, 0);
                    user.completedWorkItem = user.organizations.reduce((total, org) => total + org.completedWorkItem, 0);
                })
            );
            console.log('third')
            // Load projects and assign users and work items
            const Projects: Project[] = [];
            await Promise.all(
                Organizations.map(async (organization) => {
                    const projects = await loadOrganizationsProjects({ organization: organization.accountName });
                    const workItems = organizationWorkItemsMap[organization.accountName] || [];

                    await Promise.all(
                        projects.map(async (project) => {
                            // Load project status
                            const projectStatus = await loadProjectStatus(organization.accountName, project.name, workItems.map(wi=>{
                                return {
                                    id: wi.id,
                                    fields: wi.revisions[wi.revisions.length - 1].fields as any
                                }
                            }));

                            // Load scope descriptor
                            const scopeDescriptor = await loadScopeDistributorUnderProject({
                                organization: organization.accountName,
                                projectId: project.id,
                            });

                            // Load users under project
                            const users = await loadUsersUnderProject({
                                organization: organization.accountName,
                                scopeDescriptor: scopeDescriptor,
                            });

                            // Filter work items for this project
                            const projectWorkItems = workItems.filter((wi) => wi.revisions[wi.revisions.length - 1].fields['System.TeamProject'] === project.name);
                            const totalWorkItem = projectWorkItems.length;
                            const completedWorkItem = projectWorkItems.filter((wi) => wi.revisions[wi.revisions.length - 1].fields['System.State'] === 'Done').length;

                            // Assign project to organization
                            organization.projects.push({ id: project.id });

                            // Assign project to users
                            if (users) {
                                users.forEach((projectUser) => {
                                    const userIndex = Users.findIndex((u) => u.user.originId === projectUser.originId);
                                    if (userIndex >= 0) {
                                        const projectAlreadyIndex = Users[userIndex].projects.findIndex((p) => p.id === project.id);
                                        if (projectAlreadyIndex >= 0) return;
                                        Users[userIndex].projects.push({
                                            id: project.id,
                                            completedWorkItem: 0,
                                            totalWorkItem: 0,
                                        });
                                    }
                                });
                            }

                            // Assign users to project
                            Projects.push({
                                ...project,
                                organization_accountId: organization.accountId,
                                users: users?.map((user) => ({ originId: user.originId })) || [],
                                organizationId: organization.accountId,
                                scopeDescriptor: scopeDescriptor,
                                totalWorkItem: totalWorkItem,
                                completedWorkItem: completedWorkItem,
                                projectStatus: projectStatus,
                            });
                        })
                    );
                })
            );
            console.log('fourth')

            // Calculate work items per user per project
            await Promise.all(
                Users.map(async (user) => {
                    const projects = Projects.filter((project) => user.projects.some((p) => p.id === project.id));
                    projects.forEach((project) => {
                        const organization = Organizations.find((org) => org.accountId === project.organization_accountId);
                        const workItems = organizationWorkItemsMap[organization?.accountName!] || [];
                        const projectWorkItems = workItems.filter(
                            (wi) =>
                                wi.revisions[wi.revisions.length - 1].fields['System.TeamProject'] === project.name &&
                                wi.revisions[wi.revisions.length - 1].fields['System.AssignedTo']?.uniqueName === user.user.mailAddress
                        );
                        const completedWorkItems = projectWorkItems.filter((wi) => wi.revisions[wi.revisions.length - 1].fields['System.State'] === 'Done');
                        const projectIndex = user.projects.findIndex((p) => p.id === project.id);
                        if (projectIndex >= 0) {
                            user.projects[projectIndex].totalWorkItem = projectWorkItems.length;
                            user.projects[projectIndex].completedWorkItem = completedWorkItems.length;
                        }
                    });
                })
            );

            //convert the all workitem to single array with organisation id

            const workItemsWithOrganizationId: WorkItemTypeCentral[] = Object.entries(organizationWorkItemsMap).flatMap(([organizationName, workItems]) => {
                const organization = Organizations.find((org) => org.accountName === organizationName);

                return workItems.map((wi) => ({
                    ...wi,
                    organizationId: organization?.accountId,
                    rev: wi.revisions[wi.revisions.length - 1].rev
                }));
            });


            return {
                Organizations,
                Users,
                Projects,
                workItemsWithOrganizationId
            };
        },
        refetchOnWindowFocus: false,
        retry: retryCount,
        refetchInterval: 1000000,
        enabled: trigger,
    });

    return {
        isLoading,
        isError,
        centralData,
        refetch: refetchProductCategories,
        isFetching,
        isFetched,
        error,
    };
}